// ============================================================
// ANSICHTSSACHE-AGENT – Karte
// ============================================================
// Die Karte ist die Grundflaeche fast jeder Seite. Sie traegt
// seit dem Umbau eine Stufe Tiefe (`shadow-e1`) statt nur einer
// Linie: auf einem grauen Grund liest sich eine Flaeche, die
// aufliegt, schneller als eine, die nur umrandet ist.
//
// Kein Glas auf Karten — die Begruendung steht in tokens.css.
// ============================================================

import { forwardRef } from "react";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button, type ButtonProps } from "./button";

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Fuer Karten, die selbst ein Ziel sind (ganze Kachel klickbar).
   * Sie heben beim Zeigen leicht ab — das ist die einzige Stelle,
   * an der eine Karte ihre Hoehe aendert.
   */
  interactive?: boolean;
}

const Card = forwardRef<HTMLDivElement, CardProps>(({ className, interactive, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      // Kein Rahmen: auf der warmen grauen Arbeitsflaeche hebt
      // sich die weisse Karte von selbst ab, und der Schatten
      // macht daraus Hoehe. Ein Rahmen zusaetzlich waere die
      // dritte Ansage fuer dieselbe Kante.
      // Der Haarring bleibt — im Dunkelmodus tragen Schatten
      // kaum, dort ist er die einzige Kante.
      "bg-bg-elevated shadow-e2 rounded-xl ring-1 ring-black/[0.04] dark:ring-white/[0.06]",
      "duration-normal ease-standard transition-[box-shadow,transform]",
      interactive && "hover:shadow-e3 cursor-pointer hover:-translate-y-0.5",
      className,
    )}
    {...props}
  />
));
Card.displayName = "Card";

/**
 * Karte in der Karte (Bestandteil im Datenblatt, Abschnitt in den
 * Einstellungen): dieselbe Gestalt eine Stufe flacher — weisser
 * Grund, Haarring und leichter Schatten statt nur einer Linie.
 * Jede Kachel, die in einer Karte steht, nimmt diese Klasse.
 */
const innerCardClass =
  "bg-bg-elevated shadow-e1 rounded-lg ring-1 ring-black/[0.05] dark:ring-white/[0.06]";

const CardHeader = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-1.5 p-5 pb-0", className)} {...props} />
  ),
);
CardHeader.displayName = "CardHeader";

const CardTitle = forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn("text-heading-sm", className)} {...props} />
  ),
);
CardTitle.displayName = "CardTitle";

/**
 * Titel mit einem Bedienteil rechts daneben.
 *
 * Die Zeile ist genau so hoch wie der Titel (22 px). Ein Knopf
 * daneben ist hoeher und ragt oben und unten gleich weit hinaus,
 * statt die Zeile aufzuspreizen — so steht der Titel in jeder
 * Karte einer Rasterzeile auf derselben Hoehe, mit Knopf oder
 * ohne. Vorher glich jede Karte das selbst aus (oder gar nicht),
 * und die Titel der unteren Reihe im Kundenblatt standen um bis
 * zu fuenf Pixel versetzt.
 */
function CardTitleRow({
  title,
  action,
  lead,
}: {
  title: React.ReactNode;
  action?: React.ReactNode;
  /** Vor dem Titel — der Klappwinkel (Regel vom 13.10.2026: Winkel vorn, „…“ ganz rechts). */
  lead?: React.ReactNode;
}) {
  return (
    <div className="flex h-[22px] items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-2">
        {lead}
        <CardTitle className="truncate">{title}</CardTitle>
      </div>
      {action && <div className="flex shrink-0 items-center gap-3">{action}</div>}
    </div>
  );
}

/**
 * Der Anlegen-Knopf im Kartenkopf — auf jeder Karte derselbe:
 * Rahmenknopf, klein, Plus davor, Beschriftung „… anlegen“.
 * Er steht immer da, leer wie gefuellt; der leere Zustand
 * darunter braucht dann keinen zweiten.
 *
 * Gestalt wie die anderen Bedienteile in Kartenkoepfen
 * (Zeitachse, Archiv-Schalter — die `Schiene` aus
 * components/liste): 28 px hoch, grau unterlegt, ohne Rahmen,
 * gedaempfte Schrift. Als Rahmenknopf mit 32 px stand er daneben
 * eine Stufe zu laut.
 */
function CardAddButton({ children, className, ...props }: Omit<ButtonProps, "variant" | "size">) {
  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn(
        "bg-bg-secondary ring-border-subtle text-caption text-text-secondary h-7 gap-1.5 rounded-[10px] px-2.5 font-normal ring-1 ring-inset",
        "hover:bg-bg-tertiary hover:text-text-primary",
        className,
      )}
      {...props}
    >
      <Plus className="h-3.5 w-3.5" aria-hidden />
      {children}
    </Button>
  );
}

const CardDescription = forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("text-body-sm text-text-secondary", className)} {...props} />
));
CardDescription.displayName = "CardDescription";

const CardContent = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("p-5 pt-4", className)} {...props} />
  ),
);
CardContent.displayName = "CardContent";

const CardFooter = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-5 pt-0", className)} {...props} />
  ),
);
CardFooter.displayName = "CardFooter";

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardTitleRow,
  CardAddButton,
  CardDescription,
  CardContent,
  innerCardClass,
};
